import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  HostListener,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  WritableSignal,
  input,
  signal,
} from '@angular/core';
import { Validators } from '@angular/forms';
import {
  ControlsOf,
  FormArray,
  FormControl,
  FormGroup,
} from '@ngneat/reactive-forms';
import { TranslocoService } from '@ngneat/transloco';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ValidationService } from '@vinnova/validation';
import { Observable, Subscription, switchMap, tap } from 'rxjs';
import { FormQuestionStandard } from '../../../core/models/form-group-mapping.model';
import { ReadonlyState } from '../../../core/models/form.model';
import {
  GlobalGoalsFormControl,
  GlobalGoalsMainGoal,
  GlobalGoalsSubGoal,
} from '../../../core/models/question/question-global-goal.model';
import { Question } from '../../../core/models/question/question.model';
import { RendererFormsQuery } from '../../../core/queries/renderer-forms.query';
import { BaseGlobalGoalsService } from '../../../core/services/base-global-goals.service';
import { QuestionValueChangeService } from '../../../core/services/question-value-change.service';
import { ValidateGlobalGoalsRequired } from './validators/global-goals-required.validator';
import { ValidateGlobalGoals } from './validators/global-goals.validator';

@UntilDestroy()
@Component({
  selector: 'lib-question-global-goals',
  templateUrl: './global-goals.component.html',
  styleUrls: ['./global-goals.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class GlobalGoalsComponent implements OnInit, OnChanges, OnDestroy {
  questionForm = input<FormGroup<ControlsOf<FormQuestionStandard>>>();
  repeaterGroupId = input<string>();
  formBuilder = input<boolean>();
  question = input<Question>();
  readOnly$: Observable<ReadonlyState>;
  valueChangeSubscription: Subscription;
  patchValueSubscription: Subscription;

  chosenGoals: WritableSignal<GlobalGoalsSubGoal[]> = signal([]);
  globalGoals: WritableSignal<GlobalGoalsMainGoal[]> = signal([]);

  globalGoalsFormGroup: GlobalGoalsFormControl;

  isMobile: WritableSignal<boolean> = signal(undefined);
  hasSelectedMainGoal = false;

  globalGoalsImage = [
    {
      code: 1,
      linkEn: 'NoPoverty.svg',
      linkSv: 'IngenFattigdom.png',
    },
    { code: 2, linkEn: 'ZeroHunger.svg', linkSv: 'IngenHunger.png' },
    {
      code: 3,
      linkEn: 'GoodHealth.svg',
      linkSv: 'GodHalsaOchValbefinnande.png',
    },
    {
      code: 4,
      linkEn: 'QualityEducation.svg',
      linkSv: 'GodUtbildning.png',
    },
    {
      code: 5,
      linkEn: 'GenderEquality.svg',
      linkSv: 'Jamstalldhet.png',
    },
    {
      code: 6,
      linkEn: 'CleanWaterSanitation.svg',
      linkSv: 'RentVattenOchSanitetForAlla.png',
    },
    {
      code: 7,
      linkEn: 'CleanEnergy.svg',
      linkSv: 'HallbarEnergiForAlla.png',
    },
    {
      code: 8,
      linkEn: 'DecentWork.svg',
      linkSv: 'AnstandigaArbetsvillkorOchEkonomiskTillvaxt.png',
    },
    {
      code: 9,
      linkEn: 'Industry.svg',
      linkSv: 'HallbarIndustriInnovationerOchInfrastruktur.png',
    },
    {
      code: 10,
      linkEn: 'ReducedInequalities.svg',
      linkSv: 'MinskadOjamlikhet.png',
    },
    {
      code: 11,
      linkEn: 'SustainableCities.svg',
      linkSv: 'HallbaraStaderOchSamhallen.png',
    },
    {
      code: 12,
      linkEn: 'ResponsibleConsumption.svg',
      linkSv: 'HallbarKonsumtionOchProduktion.png',
    },
    {
      code: 13,
      linkEn: 'Climate.svg',
      linkSv: 'BekampaKlimatforandringar.png',
    },
    {
      code: 14,
      linkEn: 'LifeBelowWater.svg',
      linkSv: 'HavOchMarinaResurser.png',
    },
    { code: 15, linkEn: 'LifeOnLand.svg', linkSv: 'Ekosystem.png' },
    {
      code: 16,
      linkEn: 'PeaceJusticeInstitutions.svg',
      linkSv: 'FredligaSamhallen.png',
    },
    {
      code: 17,
      linkEn: 'Partnerships.svg',
      linkSv: 'GenomforandeOchPartnerskap.png',
    },
  ];

  get activeLang() {
    return this.translocoService.getActiveLang();
  }

  get value() {
    return this.questionForm().get('value');
  }

  constructor(
    private translocoService: TranslocoService,
    private globalGoalsService: BaseGlobalGoalsService,
    private validationService: ValidationService,
    private cdr: ChangeDetectorRef,
    private questionValueChangeService: QuestionValueChangeService,
    private formsQuery: RendererFormsQuery,
  ) {}

  ngOnInit() {
    this.readOnly$ = this.formsQuery.selectReadOnly();
    this.checkwindowWidth();
    this.globalGoalsFormGroup = this.createGlobalGoalsFormGroup();
    this.globalGoalsService
      .getGlobalGoals()
      .pipe(
        tap((val) => {
          this.globalGoals.set(
            val.map((v) => {
              return {
                ...v,

                linkSv: this.globalGoalsImage.find((i) => i.code === v.code)
                  .linkSv,
                linkEn: this.globalGoalsImage.find((i) => i.code === v.code)
                  .linkEn,
              };
            }),
          );
          this.globalGoalsFormGroup = this.createGlobalGoalsFormGroup();
        }),
        switchMap(() => this.globalGoalsFormGroup.valueChanges),
        untilDestroyed(this),
      )
      .subscribe(() => this.updateAnswer());

    this.questionValueChangeService.patchInitialValue(
      this.question().id,
      this.repeaterGroupId(),
      this.value,
      this.question(),
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['repeaterGroupId']) {
      if (this.valueChangeSubscription) {
        this.valueChangeSubscription.unsubscribe();
      }
      if (this.patchValueSubscription) {
        this.patchValueSubscription.unsubscribe();
      }
      this.questionForm().get('value').addValidators(ValidateGlobalGoals);
      this.questionForm().get('value').updateValueAndValidity();

      this.valueChangeSubscription = this.questionValueChangeService
        .valueChangeListener(
          this.value,
          {
            questionId: this.question().id,
            repeaterGroupId: this.repeaterGroupId(),
          },
          this.question(),
        )
        .pipe(untilDestroyed(this))
        .subscribe();

      this.patchValueSubscription = this.questionValueChangeService
        .patchValueListener(this.question(), this.repeaterGroupId(), this.value)
        .pipe(untilDestroyed(this))
        .subscribe(() => this.setReadonly());
    }
  }

  ngOnDestroy(): void {
    if (this.valueChangeSubscription) {
      this.valueChangeSubscription.unsubscribe();
    }
    if (this.patchValueSubscription) {
      this.patchValueSubscription.unsubscribe();
    }
  }
  disableCheckboxes(selectedGoals: string[]) {
    if (selectedGoals.length === 3)
      this.globalGoalsFormGroup.controls.goals.disable({ emitEvent: false });
    else this.globalGoalsFormGroup.controls.goals.enable({ emitEvent: false });
  }

  checkboxOptions(subGoals: GlobalGoalsSubGoal[]) {
    return subGoals.map((subGoal) => {
      return {
        id: subGoal.code,
        label: this.activeLang === 'sv' ? subGoal.nameSv : subGoal.nameEn,
        tooltip:
          this.activeLang === 'sv'
            ? subGoal.originalNameSv
            : subGoal.originalNameEn,
        checked: this.chosenGoals().some(
          (chosenGoal) => chosenGoal.code === subGoal.code,
        ),
      };
    });
  }

  selectMainGoal(goal: GlobalGoalsMainGoal, key?: string) {
    goal.selected = !goal.selected;
    this.hasSelectedMainGoal = this.globalGoals().some((g) => g.selected);
    setTimeout(() => {
      const subGoal = document.querySelector(
        '.global-goal-checkbox .form-check-input',
      ) as HTMLElement;
      if (subGoal && !key) subGoal.focus();
    }, 100);
  }

  private createGlobalGoalsFormGroup(): GlobalGoalsFormControl {
    const parsed = JSON.parse(this.questionForm().value.value);
    const formGroup = new FormGroup({
      goals: new FormArray(
        [],
        [Validators.required, ValidateGlobalGoalsRequired],
      ),
    });
    const savedGoals = [];
    for (let i = 0; this.globalGoals().length > i; i++) {
      let codesToAdd = undefined;

      if (
        parsed?.goals &&
        parsed?.goals.some((g) => g?.mainGoalCode === i + 1)
      ) {
        codesToAdd = parsed?.goals.map((g) => {
          if (g.mainGoalCode === i + 1) return g.code;
        });
      }

      const formControl = new FormControl<string>(
        codesToAdd ? JSON.stringify(codesToAdd) : undefined,
      );
      formGroup.controls.goals.push(formControl);
      if (codesToAdd) savedGoals.push(codesToAdd);
    }

    this.setChosenGoals(savedGoals.flat());
    return formGroup;
  }

  private updateAnswer() {
    if (this.globalGoalsFormGroup.controls.goals.value) {
      const selectedGoals =
        this.globalGoalsFormGroup.controls.goals.value.filter(
          (goal) => goal !== null,
        );
      if (selectedGoals.length > 0) {
        const selectedGoalsParsed = selectedGoals
          .flatMap((d) => JSON.parse(d.toString()))
          .filter((goal) => goal !== null);

        this.setChosenGoals(selectedGoalsParsed);

        this.disableCheckboxes(selectedGoalsParsed);
      } else {
        this.chosenGoals.set([]);
        this.disableCheckboxes([]);
      }
    }

    const stringfiedAnswer = JSON.stringify({
      goals: this.chosenGoals(),
    });
    this.value.markAsDirty();
    this.questionForm().patchValue({ value: stringfiedAnswer });
  }

  setChosenGoals(selectedGoals) {
    const newChosenGoals = this.globalGoals()
      .flatMap((mainGoal) => mainGoal.subGoals)
      .filter((subGoal) => selectedGoals.includes(subGoal.code));

    this.chosenGoals.set(newChosenGoals);
  }

  removeSubGoal(subGoalToRemoveCode: string) {
    const mainGoalCode = parseInt(subGoalToRemoveCode.split('_')[0]) - 1;

    // eslint-disable-next-line unicorn/prefer-structured-clone
    const parsedGoals = JSON.parse(
      JSON.stringify(this.globalGoalsFormGroup.controls.goals.value),
    );

    const controlWithoutRemoved = JSON.parse(parsedGoals[mainGoalCode]).filter(
      (pG) => pG !== subGoalToRemoveCode,
    );
    parsedGoals[mainGoalCode] = JSON.stringify(controlWithoutRemoved);
    this.value.markAsDirty();
    this.globalGoalsFormGroup.controls.goals.markAsDirty();

    this.globalGoalsFormGroup.controls.goals.patchValue(parsedGoals);
  }

  validateControl(control, directValidation, delayedValidation) {
    return this.validationService.validateControl(
      control,
      directValidation,
      delayedValidation,
    );
  }

  setReadonly() {
    let selectedGoals: string[] = [];
    const parsed = JSON.parse(this.value.value);
    if (parsed && parsed.goals) {
      selectedGoals = parsed.goals.map((g) => g.code);
    }

    this.setChosenGoals(selectedGoals);
    if (this.formsQuery.getReadOnly()) {
      this.globalGoalsFormGroup = this.createGlobalGoalsFormGroup();

      this.globalGoalsFormGroup.valueChanges
        .pipe(untilDestroyed(this))
        .subscribe(() => this.updateAnswer());
    }

    this.cdr.markForCheck();
  }

  onKeyDown($event, goal) {
    if ($event.key === 'Enter') {
      this.selectMainGoal(goal, $event.key);
    }
  }

  @HostListener('keydown.enter', ['$event'])
  onKeyEnter(event) {
    event.preventDefault();
  }

  errorMessage(control) {
    if (this.validateControl(control, ['required'], [])) {
      return this.translocoService.translate(
        'question_agenda_select_empty_field_validation',
      );
    }
    if (this.validateControl(control, ['validGlobalGoals'], [])) {
      return this.translocoService.translate(
        'question_agenda_select_sub_goal_validation',
      );
    }
    if (this.validateControl(control, ['maxlength'], [])) {
      return this.translocoService.translate('validation_maxlength');
    }
  }

  @HostListener('window:resize', ['$event'])
  private checkwindowWidth() {
    const vw = Math.max(
      document.documentElement.clientWidth || 0,
      window.innerWidth || 0,
    );
    if (vw > 768) {
      this.isMobile.set(false);
    } else if (vw <= 768) {
      this.isMobile.set(true);
    }
  }
}
